import React, { useMemo, useState } from "react";
import axios from "axios";
import { flashError, flashSuccess, useDispatch } from "@/shared/components/flash";
import AwardApprovalPopup from "../../Home/components/AwardApprovalPopup";

/**
 * ICOV3-1701: the "Pending" row action. Reuses the existing approval modal
 * (AwardApprovalPopup) in perRequestMode so a participant with multiple
 * requests awaiting the current manager can have each decided independently,
 * rather than the single shared batch decision PendingAwardApprovals.jsx uses.
 *
 * Data comes from participants.budgetCascadingApproval.pending_for_current_manager
 * (already fetched by the participant-sort call that rendered this row) — this
 * modal makes no fetch of its own. participant_first_name/last_name aren't on
 * the raw bca row (only the rich getBudgetCascadingApprovalByManagerId() shape
 * has them), so they're mapped in here from the row's own name fields.
 */
// award_data.message is stored as the rich-text HTML the requestor authored
// (see SendRewardModal's editable "Message" field) — strip tags for this
// plain-text summary rather than rendering raw HTML or showing literal <p> tags.
const stripHtml = (html) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

const PendingApprovalModal = ({
  isOpen,
  setOpen,
  toggle,
  auth,
  organization,
  program,
  participants: participant,
  setRefreshUsers,
  refreshApprovals,
}) => {
  const dispatch = useDispatch();
  const [decidingId, setDecidingId] = useState(null);

  const pendingRequests = useMemo(() => {
    const raw = participant?.budgetCascadingApproval?.pending_for_current_manager || [];
    return raw.map((request) => {
      let message = "";
      try {
        const parsed = request.award_data ? JSON.parse(request.award_data) : null;
        message = parsed?.message ? stripHtml(parsed.message) : "";
      } catch (e) {
        message = "";
      }
      return {
        ...request,
        participant_first_name: participant?.first_name,
        participant_last_name: participant?.last_name,
        message,
      };
    });
  }, [participant]);

  const onDecideOne = (request, decision, note) => {
    if (decision === "reject" && !note) {
      flashError(dispatch, "A note is required to deny a request.");
      return;
    }

    setDecidingId(request.id);
    axios
      .put(`/organization/${organization?.id}/program/${program?.id}/budget-cascading-approval`, {
        budget_cascading_approval_id: [request.id],
        approved: decision === "approved" ? 1 : 2,
        manager_id: 0,
        // A typed approval note is real "Approval notes" content and must be
        // sent, not discarded — only fall back to the default audit string
        // when the manager left it blank on approve. Deny always requires one.
        rejection_note: decision === "approved"
          ? (note && note.trim() ? note : `Approved by ${auth?.name}`)
          : note,
      })
      .then((response) => {
        if (response.status === 200) {
          flashSuccess(dispatch, "Award Approval status updated successfully!");
          setDecidingId(null);
          setRefreshUsers(true);
          refreshApprovals?.();
          if (pendingRequests.length <= 1) {
            toggle();
          }
        }
      })
      .catch((error) => {
        const reason = error.response?.data?.reason;
        if (reason === "already_processed") {
          flashError(dispatch, "This request was already processed by another manager.");
        } else if (reason === "unauthorized") {
          flashError(dispatch, "You're not authorized to act on this request.");
        } else {
          flashError(dispatch, error.message);
        }
        setDecidingId(null);
        // Either way, the row/count may be stale — refresh.
        setRefreshUsers(true);
        refreshApprovals?.();
      });
  };

  return (
    <AwardApprovalPopup
      isOpen={isOpen}
      setOpen={setOpen}
      toggle={toggle}
      awardApprovalParticipants={pendingRequests}
      perRequestMode
      onDecideOne={onDecideOne}
      loading={decidingId !== null}
      decidingId={decidingId}
      programName={program?.name}
    />
  );
};

export default PendingApprovalModal;
